import type { ReactNode } from "react";
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom";

import type { ShellOutletContext } from "../nav/AppShell";
import { MasterDetailLayout } from "../components/shell/MasterDetailLayout";
import { WorkbenchLayout } from "../components/shell/WorkbenchLayout";
import { ResultFooter } from "../components/filters/ConsoleFilterContract";
import { fetchAlertQueue } from "../data/alert-queue";
import {
  fetchChannelUnreadSummary,
  hasBoardUnread,
  type ChannelUnreadSummaryLoad,
} from "../data/channel-unread";
import { CHANNELS_MANAGE_PERMISSION, fetchChannels, type ChannelsLoad } from "../data/channels";
import {
  inboxFooterSegmentForKind,
  inboxResultCopy,
} from "../data/console-filter-contract";
import { fetchFeedPosts, type FeedLoad } from "../data/feed";
import {
  actionInboxRows,
  boardInboxRows,
  buildInboxRows,
  criticalInboxRows,
  inboxBadgeCounts,
  messageInboxRows,
  type InboxBadgeCounts,
  type InboxRow,
  type InboxRowKind,
  type InboxSegment,
} from "../data/inbox";
import {
  fetchMessageThreads,
  type MessageLoad,
  type MessageThread,
} from "../data/messages";
import { useReadModel, type AsyncResult } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";
import { MessageThread as MessageThreadSurface } from "../surfaces/MessageThread";
import { ChannelsPage } from "./ChannelsPage";
import { FeedPage } from "./FeedPage";

const SEGMENT_COPY: Readonly<
  Record<InboxSegment, { readonly title: string; readonly note: string }>
> = {
  all: {
    title: "Inbox",
    note: "Critical first. Then Action, Boards, Messages.",
  },
  critical: {
    title: "Inbox · Critical",
    note: "Life safety. Never a message thread.",
  },
  action: {
    title: "Inbox · Action",
    note: "Notices that need acknowledgement. Not chat.",
  },
  boards: {
    title: "Inbox · Boards",
    note: "Announce only. No reply.",
  },
  messages: {
    title: "Inbox · Messages",
    note: "Direct and crew. Not announcements.",
  },
};

export function inboxSegmentFromPath(pathname: string): InboxSegment {
  const segment = pathname.split("/").filter(Boolean)[1];
  if (segment === "critical" || segment === "action" || segment === "boards" || segment === "messages") {
    return segment;
  }
  return "all";
}

export function InboxPage() {
  const { config } = useOutletContext<ShellOutletContext>();

  // useReadModel intentionally retains its last successful payload during dependency changes.
  // Remount the whole Inbox host composition on a workspace switch so rows from the prior
  // account cannot remain visible while the next account's independent reads settle.
  return <AccountInboxPage key={config.accountId} />;
}

function AccountInboxPage() {
  const location = useLocation();
  const { accountRole, client, config, can } = useOutletContext<ShellOutletContext>();
  const segment = inboxSegmentFromPath(location.pathname);
  const copy = SEGMENT_COPY[segment];
  const canPublishBoards = can(CHANNELS_MANAGE_PERMISSION);
  const boardUnread = useReadModel<ChannelUnreadSummaryLoad>(
    () => fetchChannelUnreadSummary(client, config.accountId),
    [client, config.accountId],
    { pollMs: config.refreshMs },
  );
  const boardsNeedAttention =
    boardUnread.status === "ready" && hasBoardUnread(boardUnread.data);
  const boardsBadge: InboxBadgeCounts["boards"] = boardsNeedAttention ? "dot" : "none";
  const messageThreads = useReadModel<MessageLoad<readonly MessageThread[]>>(
    () => fetchMessageThreads(client, config.accountId),
    [client, config.accountId],
    { pollMs: config.refreshMs },
  );
  const readyMessageThreads =
    messageThreads.data?.status === "ready" ? messageThreads.data.value : [];
  const messagesBadge: InboxBadgeCounts["messages"] = readyMessageThreads.some(
    (thread) => thread.unreadPostCount > 0,
  )
    ? "dot"
    : "none";

  // Shell v3 (product lock 2026-08-15, lock 2): Inbox is the master-detail route. All and
  // Critical render the typed queue + record split; Action and Boards keep their host
  // surfaces (Feed roll-up, announcement boards) inside the workbench head+scroller shape
  // so the hosts stay authoritative under the zero-padding stage chrome.
  if (segment === "action" || segment === "boards") {
    return (
      <WorkbenchLayout
        className="inbox-host"
        head={
          <InboxHead copy={copy}>
            <InboxTabs
              active={segment}
              counts={{ boards: boardsBadge, messages: messagesBadge }}
              canPublish={canPublishBoards}
            />
          </InboxHead>
        }
      >
        {segment === "action" ? <FeedPage embedded /> : <ChannelsPage embedded />}
      </WorkbenchLayout>
    );
  }

  if (segment === "messages") {
    return (
      <MessagesInboxWorkspace
        accountRole={accountRole}
        boardsBadge={boardsBadge}
        load={messageThreads.data}
        loadError={messageThreads.error}
        loadStatus={messageThreads.status}
        messagesBadge={messagesBadge}
        onThreadChanged={messageThreads.reload}
      />
    );
  }

  if (segment === "critical") {
    return (
      <CriticalInboxWorkspace
        boardsBadge={boardsBadge}
        messagesBadge={messagesBadge}
      />
    );
  }
  return (
    <AllInboxWorkspace
      accountRole={accountRole}
      boardsNeedAttention={boardsNeedAttention}
      messageThreads={messageThreads}
    />
  );
}

function InboxHead({
  copy,
  children,
}: {
  readonly copy: { readonly title: string; readonly note: string };
  readonly children?: ReactNode;
}) {
  return (
    <>
      <p className="inbox-page__eyebrow">Dispatch</p>
      <h1 className="page__title">{copy.title}</h1>
      <p className="page__note">{copy.note}</p>
      {children}
    </>
  );
}

function AllInboxWorkspace({
  accountRole,
  boardsNeedAttention,
  messageThreads,
}: {
  readonly accountRole: string | null;
  readonly boardsNeedAttention: boolean;
  readonly messageThreads: AsyncResult<MessageLoad<readonly MessageThread[]>>;
}) {
  const { client, config } = useOutletContext<ShellOutletContext>();
  const alerts = useReadModel(
    () => fetchAlertQueue(client, config.accountId),
    [client, config.accountId],
    { pollMs: config.refreshMs },
  );
  const feed = useReadModel<FeedLoad>(
    () => fetchFeedPosts(client, config.accountId),
    [client, config.accountId],
    { pollMs: config.refreshMs },
  );
  const channels = useReadModel<ChannelsLoad>(
    () => fetchChannels(client, config.accountId),
    [client, config.accountId],
    { pollMs: config.refreshMs },
  );

  const posts = feed.data?.status === "ready" ? feed.data.posts : [];
  const channelRows = channels.data?.status === "ready" ? channels.data.channels : [];
  const threadRows = messageThreads.data?.status === "ready" ? messageThreads.data.value : [];
  const rows = buildInboxRows({
    alerts: alerts.data ?? [],
    posts,
    channels: channelRows,
    threads: threadRows,
  });
  const counts = {
    ...inboxBadgeCounts(rows),
    boards: boardsNeedAttention ? "dot" : "none",
  } satisfies InboxBadgeCounts;

  return (
    <InboxWorkspace
      rows={rows}
      segment="all"
      timeZone={config.displayTimeZone}
      header={
        <InboxHead copy={SEGMENT_COPY.all}>
          <InboxTabs active="all" counts={counts} canPublish={false} />
        </InboxHead>
      }
      sections={[
        {
          kind: "sos",
          label: "Critical",
          empty: "No open Critical items.",
          state: plainReadState(alerts.status, alerts.error),
        },
        {
          kind: "feed_ack",
          label: "Action",
          empty: "No Feed posts yet.",
          state: hostLoadState(feed.status, feed.error, feed.data),
        },
        {
          kind: "board",
          label: "Boards · announcements",
          empty: "No announcement boards yet.",
          state: hostLoadState(channels.status, channels.error, channels.data),
          ...(boardsNeedAttention ? { attention: "Boards need attention" } : {}),
        },
        {
          kind: "message",
          label: "Messages · direct & crew",
          empty: "No messages yet. Direct and crew threads land here. Site announcements stay on Boards. Life safety stays in Critical.",
          state: messageLoadState(messageThreads.status, messageThreads.error, messageThreads.data),
          ...(counts.messages === "dot" ? { attention: "Messages need attention" } : {}),
        },
      ]}
      accountRole={accountRole}
      onThreadChanged={messageThreads.reload}
    />
  );
}

function CriticalInboxWorkspace({
  boardsBadge,
  messagesBadge,
}: {
  readonly boardsBadge: InboxBadgeCounts["boards"];
  readonly messagesBadge: InboxBadgeCounts["messages"];
}) {
  const { client, config } = useOutletContext<ShellOutletContext>();
  const alerts = useReadModel(
    () => fetchAlertQueue(client, config.accountId),
    [client, config.accountId],
    { pollMs: config.refreshMs },
  );
  const rows = criticalInboxRows(alerts.data ?? []);
  const counts = inboxBadgeCounts(rows);

  return (
    <InboxWorkspace
      rows={rows}
      segment="critical"
      timeZone={config.displayTimeZone}
      header={
        <InboxHead copy={SEGMENT_COPY.critical}>
          <InboxTabs
            active="critical"
            counts={{ critical: counts.critical, boards: boardsBadge, messages: messagesBadge }}
            canPublish={false}
          />
          <p className="inbox-page__critical-rule" role="note">
            This is not a reply thread. Inbox has no action that acknowledges, resolves, or stands down an alert.
          </p>
        </InboxHead>
      }
      sections={[
        {
          kind: "sos",
          label: "Critical",
          empty: "No open Critical items.",
          state: plainReadState(alerts.status, alerts.error),
        },
      ]}
    />
  );
}

function MessagesInboxWorkspace({
  accountRole,
  boardsBadge,
  load,
  loadError,
  loadStatus,
  messagesBadge,
  onThreadChanged,
}: {
  readonly accountRole: string | null;
  readonly boardsBadge: InboxBadgeCounts["boards"];
  readonly load: MessageLoad<readonly MessageThread[]> | null;
  readonly loadError: string | null;
  readonly loadStatus: "loading" | "ready" | "error";
  readonly messagesBadge: InboxBadgeCounts["messages"];
  readonly onThreadChanged: () => void;
}) {
  const { config } = useOutletContext<ShellOutletContext>();
  const threads = load?.status === "ready" ? load.value : [];
  return (
    <InboxWorkspace
      accountRole={accountRole}
      rows={messageInboxRows(threads)}
      segment="messages"
      timeZone={config.displayTimeZone}
      header={
        <InboxHead copy={SEGMENT_COPY.messages}>
          <InboxTabs
            active="messages"
            counts={{ boards: boardsBadge, messages: messagesBadge }}
            canPublish={false}
          />
        </InboxHead>
      }
      sections={[
        {
          kind: "message",
          label: "Messages · direct & crew",
          empty: "No messages yet. Direct and crew threads land here. Site announcements stay on Boards. Life safety stays in Critical.",
          state: messageLoadState(loadStatus, loadError, load),
          ...(messagesBadge === "dot" ? { attention: "Messages need attention" } : {}),
        },
      ]}
      onThreadChanged={onThreadChanged}
    />
  );
}

function InboxTabs({
  active,
  counts = {},
  canPublish,
}: {
  readonly active: InboxSegment;
  readonly counts?: Partial<InboxBadgeCounts>;
  readonly canPublish: boolean;
}) {
  const tabs: readonly {
    readonly segment: InboxSegment;
    readonly label: string;
    readonly to: string;
  }[] = [
    { segment: "all", label: "All", to: "/inbox" },
    { segment: "critical", label: "Critical", to: "/inbox/critical" },
    { segment: "action", label: "Action", to: "/inbox/action" },
    { segment: "boards", label: "Boards", to: "/inbox/boards" },
    { segment: "messages", label: "Messages", to: "/inbox/messages" },
  ];

  return (
    <nav className="inbox-tabs" aria-label="Inbox sections">
      <div className="inbox-tabs__segments">
        {tabs.map((tab) => {
          const count = tab.segment === "critical" ? counts.critical : tab.segment === "action" ? counts.action : undefined;
          const dot =
            (tab.segment === "boards" && counts.boards === "dot") ||
            (tab.segment === "messages" && counts.messages === "dot");
          return (
            <Link
              key={tab.segment}
              to={tab.to}
              className={tab.segment === active ? "inbox-tabs__link inbox-tabs__link--active" : "inbox-tabs__link"}
              aria-current={tab.segment === active ? "page" : undefined}
            >
              {tab.label}
              {typeof count === "number" && count > 0 ? (
                <span className="inbox-tabs__count" aria-label={`${count} items`}>
                  {count}
                </span>
              ) : null}
              {dot ? (
                <span
                  className="inbox-tabs__dot"
                  aria-label={`${tab.label} need attention`}
                  title={`${tab.label} need attention`}
                />
              ) : null}
            </Link>
          );
        })}
      </div>
      {canPublish && active === "boards" ? (
        <Link className="inbox-tabs__publish" to="/inbox/boards#boards-publish">
          Publish
        </Link>
      ) : null}
    </nav>
  );
}

interface InboxSectionState {
  readonly kind: InboxRowKind;
  readonly label: string;
  readonly empty: string;
  readonly state: { readonly message: string; readonly error: boolean } | null;
  readonly attention?: string;
}

function InboxWorkspace({
  accountRole = null,
  rows,
  segment,
  timeZone,
  sections,
  header,
  onThreadChanged = () => {},
}: {
  readonly accountRole?: string | null;
  readonly rows: readonly InboxRow[];
  readonly segment: "all" | "critical" | "messages";
  readonly timeZone: string;
  readonly sections: readonly InboxSectionState[];
  readonly header: ReactNode;
  readonly onThreadChanged?: () => void;
}) {
  const { client, config } = useOutletContext<ShellOutletContext>();
  const [searchParams] = useSearchParams();
  const requestedValue = searchParams.get(segment === "messages" ? "thread" : "item");
  const requestedKey =
    requestedValue === null
      ? null
      : segment === "messages"
        ? `message:${requestedValue}`
        : requestedValue;
  // An explicit ?item selection is what pushes the detail over the list on narrow widths;
  // on the split widths the first row stands in so the record pane is never a void.
  const requested =
    requestedKey !== null ? (rows.find((row) => row.key === requestedKey) ?? null) : null;
  const implicit = rows[0] ?? null;
  // Opening Inbox is not opening a message. Message threads require an explicit row selection
  // before their posts load or receipt RPCs run, so unread cannot clear from a pane stand-in.
  const selected = requested ?? (implicit?.kind === "message" ? null : implicit);
  const selectionBase =
    segment === "all" ? "/inbox" : segment === "critical" ? "/inbox/critical" : "/inbox/messages";

  return (
    <MasterDetailLayout
      className="inbox-mdl"
      detailOpen={requested !== null}
      list={
        <>
          <div className="inbox-pane__head">{header}</div>
          <section className="inbox-pane__scroll" aria-label="Inbox work list">
            {sections.map((section) => {
              const sectionRows = rows.filter((row) => row.kind === section.kind);
              const showNumericCount =
                (section.kind === "sos" || section.kind === "feed_ack") && sectionRows.length > 0;
              const footer =
                section.state === null && sectionRows.length > 0
                  ? inboxResultCopy({
                      segment: inboxFooterSegmentForKind(section.kind),
                      shownCount: sectionRows.length,
                    })
                  : null;
              return (
                <section className="inbox-group" key={section.kind} aria-labelledby={`inbox-${section.kind}-label`}>
                  <div className="inbox-group__head">
                    <h2 id={`inbox-${section.kind}-label`}>{section.label}</h2>
                    {section.attention !== undefined || showNumericCount ? (
                      <div className="inbox-group__signals">
                        {section.attention !== undefined ? (
                          <span className="inbox-group__attention">{section.attention}</span>
                        ) : null}
                        {showNumericCount ? <span>{sectionRows.length}</span> : null}
                      </div>
                    ) : null}
                  </div>
                  {section.state !== null ? (
                    <p
                      className={
                        section.state.error
                          ? section.kind === "message"
                            ? "inbox-group__state inbox-group__state--message-error"
                            : "inbox-group__state inbox-group__state--error"
                          : "inbox-group__state"
                      }
                      role={section.state.error ? "alert" : "status"}
                    >
                      {section.state.message}
                    </p>
                  ) : sectionRows.length === 0 ? (
                    <p className="inbox-group__state">{section.empty}</p>
                  ) : (
                    <ul className="inbox-rows">
                      {sectionRows.map((row) => (
                        <li key={row.key}>
                          <Link
                            to={
                              segment === "messages" && row.kind === "message"
                                ? `${selectionBase}?thread=${encodeURIComponent(row.threadId)}#inbox-detail`
                                : `${selectionBase}?item=${encodeURIComponent(row.key)}#inbox-detail`
                            }
                            className={inboxRowClass(row, row.key === selected?.key)}
                            aria-current={row.key === selected?.key ? "true" : undefined}
                            aria-controls="inbox-detail"
                          >
                            <span className={`inbox-row__kind inbox-row__kind--${row.kind}`}>
                              {rowKindLabel(row)}
                            </span>
                            <span className="inbox-row__title">{row.title}</span>
                            <span className="inbox-row__summary">{row.summary}</span>
                            {(row.kind === "board" || row.kind === "message") && row.unread ? (
                              <span className="inbox-row__unread" aria-label={`Unread ${row.kind}`} />
                            ) : null}
                            <span className="inbox-row__time">{formatDisplayTime(row.occurredAt, timeZone)}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  {footer !== null ? (
                    <ResultFooter state={footer.state} foot={footer.foot} />
                  ) : null}
                </section>
              );
            })}
          </section>
        </>
      }
      detail={
        <>
          <Link className="mdl-back" to={selectionBase}>
            Back to list
          </Link>
          <div id="inbox-detail" className="inbox-pane__scroll inbox-pane__scroll--detail" tabIndex={-1}>
            {selected === null ? (
              <div className="inbox-detail inbox-detail--empty">
                <h2>{rows.some((row) => row.kind === "message") ? "Select a message thread" : "No item selected"}</h2>
                <p>
                  {rows.some((row) => row.kind === "message")
                    ? "Choose a Direct or Crew row to open its connected history."
                    : "New Critical, Action, Board, and Message items will appear here when their hosts return them."}
                </p>
              </div>
            ) : selected.kind === "message" ? (
              <div key={selected.key} className="mo-detail-enter">
                <MessageThreadSurface
                  accountId={config.accountId}
                  accountRole={accountRole}
                  client={client}
                  onThreadChanged={onThreadChanged}
                  pollMs={config.refreshMs}
                  threadId={selected.threadId}
                  timeZone={timeZone}
                />
              </div>
            ) : (
              // Keyed by the selection so the record cross-fades (locked 140ms, opacity
              // only) while the list row settles instantly.
              <div key={selected.key} className="mo-detail-enter">
                <InboxDetail row={selected} timeZone={timeZone} />
              </div>
            )}
          </div>
        </>
      }
    />
  );
}

/** Critical rows stay visually louder than Action / Boards at every width (board frame 03);
 *  selection stays blue per the color doctrine, so loud and selected never collide. */
function inboxRowClass(row: InboxRow, isSelected: boolean): string {
  const classes = ["inbox-row"];
  if (row.kind === "sos") {
    classes.push("inbox-row--critical");
  }
  if (isSelected) {
    classes.push("inbox-row--selected");
  }
  return classes.join(" ");
}

function InboxDetail({ row, timeZone }: { readonly row: InboxRow; readonly timeZone: string }) {
  if (row.kind === "sos") {
    return (
      <article className="inbox-detail inbox-detail--critical">
        <p className="inbox-detail__eyebrow">Critical</p>
        <h2>{row.title}</h2>
        <p className="inbox-detail__meta">
          {row.summary} · {formatDisplayTime(row.occurredAt, timeZone)}
        </p>
        <p className="inbox-detail__rule">
          Dispatch owns response, coordination, and outcome. Selecting this item changes no alert state.
        </p>
        <div className="inbox-detail__actions">
          {/* Route only. Inbox owns no act on an alert, and the label names the surface the
              link actually opens: the SOS response surface, or the alert queue. */}
          <Link className="inbox-detail__primary" to={row.hostHref}>
            {row.isSos ? "Open SOS response" : "Open in Alerts"}
          </Link>
          {row.mapHref !== null ? (
            <Link className="inbox-detail__secondary" to={row.mapHref}>
              Focus map
            </Link>
          ) : null}
        </div>
      </article>
    );
  }

  if (row.kind === "feed_ack") {
    return (
      <article className="inbox-detail">
        <p className="inbox-detail__eyebrow">Action / Feed</p>
        <h2>{row.title}</h2>
        <p className="inbox-detail__meta">
          {row.authorDisplayName} · {row.audienceLabel} · {formatDisplayTime(row.occurredAt, timeZone)}
        </p>
        <p className="inbox-detail__body">{row.body}</p>
        <p className="inbox-detail__rule">
          {row.requiresAck
            ? `${row.acknowledgementCount} acknowledged. Open the Feed roll-up for the roster and follow-up.`
            : "This Feed post does not require acknowledgement."}
        </p>
        <div className="inbox-detail__actions">
          <Link className="inbox-detail__primary" to={row.hostHref}>
            Open Feed roll-up
          </Link>
        </div>
      </article>
    );
  }

  return (
    <article className="inbox-detail">
      <p className="inbox-detail__eyebrow">Board</p>
      <h2>{row.title}</h2>
      <p className="inbox-detail__meta">
        {row.summary} · {formatDisplayTime(row.occurredAt, timeZone)}
      </p>
      <p className="inbox-detail__rule">
        Boards are announcement surfaces. Open the board to publish or review its history.
      </p>
      <div className="inbox-detail__actions">
        <Link className="inbox-detail__primary" to={row.hostHref}>
          Open announcement board
        </Link>
      </div>
    </article>
  );
}

function rowKindLabel(row: InboxRow): string {
  if (row.kind === "sos") return row.isSos ? "SOS" : "Alert";
  if (row.kind === "feed_ack") return row.requiresAck ? "Needs acknowledgement" : "Feed";
  if (row.kind === "board") return "Board";
  return row.threadKind === "direct" ? "Direct" : "Crew";
}

function plainReadState(
  status: "loading" | "ready" | "error",
  error: string | null,
): { readonly message: string; readonly error: boolean } | null {
  if (status === "loading") return { message: "Loading Critical items...", error: false };
  if (status === "error") return { message: error ?? "Could not load Critical items.", error: true };
  return null;
}

function hostLoadState(
  status: "loading" | "ready" | "error",
  error: string | null,
  load: FeedLoad | ChannelsLoad | null,
): { readonly message: string; readonly error: boolean } | null {
  if (status === "loading" && load === null) return { message: "Loading...", error: false };
  if (status === "error") return { message: error ?? "Could not load this section.", error: true };
  if (load?.status === "unavailable") return { message: load.reason, error: false };
  if (load?.status === "failed") return { message: load.reason, error: true };
  return null;
}

function messageLoadState(
  status: "loading" | "ready" | "error",
  error: string | null,
  load: MessageLoad<readonly MessageThread[]> | null,
): { readonly message: string; readonly error: boolean } | null {
  if (status === "loading" && load === null) return { message: "Loading Messages...", error: false };
  if (status === "error") return { message: error ?? "Could not load Messages.", error: true };
  if (load?.status === "unavailable") return { message: load.reason, error: false };
  if (load?.status === "failed") return { message: load.reason, error: true };
  return null;
}
